import {
  RepeatRounded,
  ShoppingBag,
  Check,
  ArrowForward,
} from '@mui/icons-material';
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import { Product } from '../../../models/Product';
import Modal from '../Modal';
import { useShoppingList } from '../../../context/ShoppingListContext';

type Props = {
  isModalVisible: boolean;
  setIsModalVisible: (value: boolean) => void;
  product: Product;
  quantity?: number;
};

function ProductAddedToList({
  isModalVisible,
  setIsModalVisible,
  product,
  quantity = 1,
}: Props) {
  const { isShoppingListOpen, setIsShoppingListOpen } = useShoppingList();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <Modal
      show={isModalVisible}
      onClose={() => setIsModalVisible(false)}
      size="md"
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="sm:min-w-[400px]"
      >
        {/* Nagłówek sukcesu */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-3 mb-6"
        >
          <div className="bg-green-50 rounded-full p-2">
            <Check className="text-green-600" />
          </div>
          <h2 className="text-xl font-medium text-gray-900">
            Produkt dodany do koszyka!
            {quantity > 1 && (
              <span className="text-gray-500 ml-1">({quantity} szt.)</span>
            )}
          </h2>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center"
        >
          <div className="h-24 w-24 bg-white rounded-md flex-shrink-0 flex items-center justify-center border border-gray-200">
            <Link to={`/produkt/${product.slug}`}>
              <img
                src={product.mobilePrimaryImage}
                alt={product.name}
                className="h-20 w-20 object-contain"
              />
            </Link>
          </div>

          <div className="ml-4 flex-grow">
            <Link
              to={`/produkt/${product.slug}`}
              className="text-gray-800 hover:text-primary transition-colors font-medium text-base line-clamp-2"
            >
              {product.name}
            </Link>

            <div className="flex items-center justify-between mt-2">
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Cena:</span>
                <span className="text-lg font-semibold text-gray-900">
                  {product.price.toFixed(2)} zł
                </span>
                <span className="text-xs text-gray-500">
                  Netto: {(product.price * 0.77).toFixed(2)} zł
                </span>
              </div>

              {quantity > 1 && (
                <div className="text-right">
                  <div className="text-sm text-gray-500">Suma:</div>
                  <div className="text-lg font-semibold text-primary">
                    {(product.price * quantity).toFixed(2)} zł
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Akcje */}
        <motion.div
          variants={itemVariants}
          className="flex sm:flex-row flex-col gap-3"
        >
          <motion.button
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-md font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
            onClick={() => setIsModalVisible(false)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RepeatRounded fontSize="small" />
            <span>Kontynuuj zakupy</span>
          </motion.button>

          <motion.button
            className="flex-1 px-4 py-2.5 bg-primary text-white rounded-md font-medium flex items-center justify-center gap-2 hover:bg-primary-dark transition-colors"
            onClick={() => {
              setIsShoppingListOpen(!isShoppingListOpen);
              setIsModalVisible(false);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ShoppingBag fontSize="small" />
            <span>Do koszyka</span>
            <ArrowForward fontSize="small" />
          </motion.button>
        </motion.div>

        {/* Dodatkowe informacje - opcjonalne */}
        <motion.div
          variants={itemVariants}
          className="mt-6 text-center text-sm text-gray-500"
        >
          {quantity === 1
            ? 'W koszyku jest teraz 1 sztuka tego produktu'
            : `W koszyku jest teraz ${quantity} sztuk tego produktu`}
        </motion.div>
      </motion.div>
    </Modal>
  );
}

export default ProductAddedToList;
